/**
 * Identidad del Gourmetero — contrato compartido entre PWA y API.
 *
 * Autoridad: docs/02-USER-STORIES-AND-ACCEPTANCE.md §3 (GU-AUTH-01..08)
 *            docs/03-CRITICAL-FLOWS-AND-ONBOARDING.md §2 (dos modos)
 *            docs/adr/004-contradicciones-resueltas.md §C8, §C12
 *
 * PRINCIPIO: "Nunca opiniones anónimas" (doc 07). Se cumple con identidad
 * persistente y atribuible, no con biometría — ver C8.
 */

import { z } from 'zod';

/**
 * Modo de entrada al producto. NO son tipos de cuenta ni planes: son dos
 * rutas hacia el mismo lugar (doc 03 §2). Un Explorador se convierte en
 * Gourmetero en cualquier momento, sin migración ni pérdida de datos.
 */
export const UserMode = z.enum(['explorador', 'gourmetero']);
export type UserMode = z.infer<typeof UserMode>;

export const AuthProvider = z.enum(['google', 'apple', 'otp_email', 'otp_phone']);
export type AuthProvider = z.infer<typeof AuthProvider>;

/** Longitud del código OTP. GU-AUTH-03. */
export const OTP_LENGTH = 6;

/** Validez del OTP en minutos. GU-AUTH-03. */
export const OTP_VALIDITY_MINUTES = 10;

/** Intentos fallidos antes de invalidar el código. GU-AUTH-03. */
export const OTP_MAX_ATTEMPTS = 5;

/** Solicitudes de OTP permitidas por destinatario en 15 minutos. GU-AUTH-03. */
export const OTP_MAX_REQUESTS_PER_WINDOW = 3;
export const OTP_REQUEST_WINDOW_MINUTES = 15;

/** Límites del avatar subido por el usuario. GU-AUTH-04. */
export const AVATAR_MAX_DIMENSION_PX = 512;
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
export const AVATAR_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

/**
 * Rutas de la galería curada de avatares.
 *
 * Vive en los contratos y no solo en la PWA porque **el servidor tiene que
 * validar contra la misma lista**: ADR-004 §C8 dice que "con cara" es
 * identidad atribuible, y aceptar una URL cualquiera dejaría poner un logo
 * de marca —o el píxel de tracking de un tercero— como cara de quien
 * recomienda. Dos copias de esta lista solo garantizan que un día
 * discrepen, y ese día el servidor rechazaría avatares que la PWA ofrece.
 *
 * Las etiquetas y el orden de presentación son de la PWA; esto es solo qué
 * se acepta.
 */
export const AVATAR_GALLERY_PATHS = [
  '/avatars/taco.jpg',
  '/avatars/montadito.jpg',
  '/avatars/cafe.jpg',
  '/avatars/dona.jpg',
  '/avatars/hotdog.jpg',
  '/avatars/pizza.jpg',
  '/avatars/ramen.jpg',
  '/avatars/sushi.jpg',
  '/avatars/dumpling.jpg',
  '/avatars/ensalada.jpg',
  '/avatars/bistec.jpg',
  '/avatars/galleta.jpg',
  '/avatars/galleta2.jpg',
  '/avatars/banana.jpg',
  '/avatars/gu.jpg',
] as const;

/** ¿Es un avatar que GU acepta como cara? */
export function isGalleryAvatar(url: string): boolean {
  return (AVATAR_GALLERY_PATHS as readonly string[]).includes(url);
}

/** Rango de tipos de comida favoritos. GU-AUTH-05. */
export const FOOD_TYPES_MIN = 1;
export const FOOD_TYPES_MAX = 5;

export const DISPLAY_NAME_MIN = 2;
export const DISPLAY_NAME_MAX = 30;

export const DisplayName = z
  .string()
  .trim()
  .min(DISPLAY_NAME_MIN)
  .max(DISPLAY_NAME_MAX);

/**
 * Perfil público de un Gourmetero. Es lo que ve cualquiera junto a una
 * recomendación: nunca se renderiza una opinión sin esto.
 */
export const GourmeteroProfile = z.object({
  id: z.string().uuid(),
  displayName: DisplayName,
  avatarUrl: z.string().url().nullable(),
  cityId: z.string().nullable(),
  favoriteFoodTypes: z.array(z.string()).max(FOOD_TYPES_MAX),
  level: z.number().int().min(1).max(4),
  xp: z.number().int().nonnegative(),
  reputationScore: z.number().min(0).max(100),
});
export type GourmeteroProfile = z.infer<typeof GourmeteroProfile>;

/** Códigos de error de autenticación, accionables en la UI. */
export const AuthError = z.enum([
  'profile_incomplete',
  'otp_invalid',
  'otp_expired',
  'otp_too_many_attempts',
  'otp_rate_limited',
  'oauth_cancelled',
  'oauth_failed',
  'session_expired',
  'display_name_taken',
]);
export type AuthError = z.infer<typeof AuthError>;

/**
 * ¿Puede este perfil publicar contenido público?
 *
 * GU-AUTH-04: la API responde 409 `profile_incomplete` si falta nombre o
 * avatar. Esta función es la única fuente de esa regla; cliente y servidor
 * la comparten para que la UI nunca ofrezca publicar y luego falle.
 *
 * Nota deliberada (C8): un avatar de galería CUENTA como avatar. "Con cara"
 * es identidad atribuible, no una fotografía del rostro.
 */
export function canPublish(profile: {
  displayName?: string | null;
  avatarUrl?: string | null;
}): boolean {
  const name = profile.displayName?.trim() ?? '';
  const avatar = profile.avatarUrl?.trim() ?? '';
  return name.length >= DISPLAY_NAME_MIN && avatar.length > 0;
}

/**
 * Acciones que exigen cuenta. Todo lo demás se hace en Modo Explorador,
 * sin registro y sin permisos (doc 03 §2.2).
 *
 * Ojo: `crear_coleccion` NO está aquí. Un Explorador arma su Colección
 * borrador en local; la cuenta se pide al NOMBRARLA, que es cuando ya
 * tiene algo que perder (doc 03 §3.3).
 */
export const GATED_ACTIONS = [
  'agregar_lugar',
  'publicar_recomendacion',
  'validar_lugar',
  'nombrar_coleccion',
  'guardar_coleccion_ajena',
] as const;

export type GatedAction = (typeof GATED_ACTIONS)[number];

export function requiresAccount(action: string): action is GatedAction {
  return (GATED_ACTIONS as readonly string[]).includes(action);
}
